<template>
  <b-container
    fluid
    class="assets-and-maneuver"
  >
    <b-row>
      <s-table
        :items="items"
        :fields="fields"
        class="assets-and-maneuver__table"
        :busy="!!loading"
      >
        <template #cell(dateAndTime)="{ item }">
          <i
            aria-hidden="true"
            v-if="item.outOfReference"
            v-b-tooltip.hover.right
            :title="$t('reports.oscillography.maneuver-grid.out-of-reference')"
            class="fas fa-exclamation-triangle mr-2"
          />
          {{ item.dateAndTime | dateTimeFilter }}
          <b-row
            no-gutters
            align-h="start"
            class="assets-and-maneuver__additional-info"
          >
            {{ $t('entities.installation') }}:
            {{ item.installationName }}
            /
            {{ item.companyName }}
          </b-row>
        </template>
        <template #cell(number)="{ item }">
          <b-row align-h="center">
          {{ $t('fields.general.operation') }}:
          {{ item.oscillographyId }}
          </b-row>
          <b-row align-h="center">
          {{ $t('fields.general.type') }}:
          {{ maneuverHandler(item.maneuverType) }}
          </b-row>
        </template>

        <template #cell(moduleName)="{ item }">
          <b-row align-h="center">
          {{ item.moduleName }}
          </b-row>
          <b-row
            no-gutters
            align-h="center"
            class="assets-and-maneuver__additional-info"
          >
            {{ $t('entities.asset') }}:
            {{ item.assetName }}
          </b-row>
        </template>

        <template #cell(qualityAchieved)="data">
          <i
            aria-hidden="true"
            v-b-tooltip.hover.right
            :title="qualityTooltipHandler(data.item)"
            :class="qualityIconHandler(data.item)"
          />
        </template>
        <template #cell(actions)="data">
          <b-row class="pr-5" no-gutters align-h="center">
            <b-btn
              variant="outline-primary"
              @click="tableActionHandler(data)"
            >
              <i
                class="fas fa-plus mr-2"
                aria-hidden="true"
              />
              {{ $t('general.add') }}
            </b-btn>
          </b-row>
        </template>
      </s-table>
    </b-row>
    <b-row class="mt-3">
      <label class="assets-and-maneuver__input-group-title"
      > {{ $t('fields.asset.summary-of-selected-maneuvers') }}
      </label>
    </b-row>
    <b-row class="assets-and-maneuver__selected-row mt-3 p-2">
      <b-col
        :key="item.id"
        cols="12"
        sm="6"
        md="4"
        class="assets-and-maneuver__badge my-1"
        v-for="(item, index) in selectedManeuver"
      >
      <b-row align-h="between" no-gutters>
        <b-col cols="11">
          <label class="assets-and-maneuver__remove-maneuver-text">
            {{ maneuverLabel(item) }}
          </label>
        </b-col>
        <b-col cols="auto">
          <i
            aria-hidden="true"
            @click="removeSelected(item, index)"
            class="fas fa-times ml-1 assets-and-maneuver__remove-maneuver-icon"
          />
        </b-col>
      </b-row>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import {
  formatter as dateTimeFormatter,
  isGreater,
  isLess,
} from '@/services/dateTime/datetimeService';
import MAX_MANEUVER_LIMIT from '@/domain/constants/reports/oscillography';

export default {
  name: 'AssetAndManeuverGrid',
  props: {
    value: {
      type: Array,
      default: () => ([]),
    },
    items: {
      type: Array,
    },
    filterBackup: {
      type: Object,
    },
    backup: {
      type: Array,
      default: () => ([]),
    },
    loading: {
      type: [Boolean, Number],
    },
    maxLimitOfManeuvers: {
      type: Number,
      default: MAX_MANEUVER_LIMIT,
    },
    activeManeuvers: {
      type: Number,
      default: 0,
    },
  },
  data: () => ({
    lastBackupSignature: '',
  }),
  computed: {
    selectedManeuver: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    fields() {
      return [
        {
          key: 'dateAndTime',
          sortable: false,
          labelPath: 'report-name',
          class: 'report-list__cell-report-name',
          label: this.$t('reports.oscillography-date'),
        },
        {
          key: 'number',
          sortable: false,
          labelPath: 'report-name',
          class: 'report-list__cell-report-name',
          label: this.$t('reports.maneuver'),
        },
        {
          key: 'moduleName',
          sortable: false,
          labelPath: 'report-name',
          label: this.$t('entities.ied'),
        },
        {
          key: 'qualityAchieved',
          sortable: false,
          labelPath: 'report-name',
          class: 'report-list__cell-report-name',
          label: this.$t('general.quality'),
        },
        {
          key: 'actions',
          sortable: false,
          labelPath: '',
          class: 'report-list__cell-report-name',
          label: '',
        },
      ];
    },
  },
  created() {
    this.handleBackup();
  },
  watch: {
    backup: {
      deep: true,
      handler(value) {
        if (!value || !value.length) return;

        const signature = value
          .map(item => `${item.oscillographyId}:${item.assetModuleId || ''}:${item.maneuverType || ''}`)
          .join('|');
        if (signature === this.lastBackupSignature) return;

        this.lastBackupSignature = signature;

        this.$emit('input', [...value]);
        this.$emit('addActiveManeuvers', [...value]);
      },
    },
  },
  methods: {
    handleBackup() {
      this.backup.forEach(item => this.selectedManeuver.push(item));
    },
    selectedChecker(item) {
      return this.selectedManeuver
        .some((selected) => {
          const { oscillographyId, assetModuleId, maneuverType } = selected;
          return oscillographyId === item.oscillographyId
          && assetModuleId === item.assetModuleId
          && maneuverType === item.maneuverType;
        });
    },
    maneuverLabel({
      dateAndTime,
      assetName,
      oscillographyId,
      maneuverType,
      installationName,
    }) {
      return `${dateTimeFormatter(dateAndTime)} - ${installationName} | ${assetName} - ${this.tableManeuverField(oscillographyId, maneuverType)}`;
    },
    tableManeuverField(oscillographyId, maneuverType) {
      return `${this.$t('fields.general.operation')}: ${oscillographyId} ${this.$t('fields.general.type')}: ${this.maneuverHandler(maneuverType)}`;
    },
    tableActionHandler({ item, index }) {
      if (this.activeManeuvers === this.maxLimitOfManeuvers) {
        this.$pushAlert({
          code: 'ME094',
          messageParams: {
            0: this.maxLimitOfManeuvers,
          },
        });

        return;
      }

      if (!this.selectedChecker(item)) {
        this.items.splice(index, 1);
        this.selectedManeuver.push(item);
        this.$emit('input', this.selectedManeuver);
        this.$emit('addActiveManeuvers', this.selectedManeuver);
        return;
      }
      this.selectedManeuver = this.selectedManeuver
        .filter((selected) => {
          const { oscillographyId, assetModuleId, maneuverType } = selected;
          return oscillographyId !== item.oscillographyId
          || assetModuleId !== item.assetModuleId
          || maneuverType !== item.maneuverType;
        });
      this.$emit('input', this.selectedManeuver);
    },
    dateRangeValidator(finalDateAndTime, initialDateAndTime, maneuverDate) {
      if (!this.filterBackup.final.date || !this.filterBackup.initial.date) {
        return true;
      }

      return isGreater(maneuverDate, initialDateAndTime)
        && isLess(maneuverDate, finalDateAndTime);
    },
    filterValidator(maneuver) {
      const {
        assets,
        maneuverTypes,
        maneuverSubTypes,
        finalDateAndTime,
        initialDateAndTime,
      } = this.filterBackup;
      const validator = [
        assets.includes(maneuver.assetId),
        maneuverTypes.includes(maneuver.maneuverType),
        maneuverSubTypes.includes(maneuver.maneuverSubType),
        this.dateRangeValidator(
          finalDateAndTime,
          initialDateAndTime,
          maneuver.dateAndTime,
        ),
      ];
      return validator.every(valid => valid);
    },
    returnManeuverToGrid(maneuver) {
      if (!this.filterValidator(maneuver)) return;
      this.$emit('removeActiveManeuvers', maneuver);
      this.$emit('updateManeuverList', maneuver);
    },
    removeSelected(removedOption, index) {
      this.selectedManeuver.splice(index, 1);
      this.$emit('input', this.selectedManeuver);
      this.returnManeuverToGrid(removedOption);
    },
    qualityIconHandler({ qualityAchieved }) {
      const baseClass = 'assets-and-maneuver__quality-icon';
      return qualityAchieved
        ? `fas fa-file-alt ${baseClass}--green` : `fas fa-file-signature ${baseClass}--yellow`;
    },
    qualityTooltipHandler({ qualityAchieved }) {
      return qualityAchieved
        ? this.$t('reports.oscillography.maneuver-grid.correct-data') : this.$t('reports.oscillography.maneuver-grid.incorrect-data');
    },
    maneuverHandler(type) {
      return this.$t(`enums.maneuver-type.${type}`);
    },
  },
};
</script>

<style lang="sass" scoped>
.assets-and-maneuver
  &__loader
    height: 630px
  &__table ::v-deep
    overflow-y: auto
    display: flex
    max-height: 53vh
    table
      display: flex
      flex-direction: column
      min-width: 800px
    thead
      position: sticky
      top: 0
    tbody, thead
      tr
        display: table
        width: 100%
        table-layout: fixed
  &__additional-info
    font-size: .85em
  &__input-group-title
    color: var(--color-secondary)
  &__quality-icon
    &--green
      font-size: 2em
      color: var(--color-primary)
    &--yellow
      font-size: 2em
      color: #F0E68C
  &__selected-row
    border: 1px solid var(--color-gray-medium-light)
    border-radius: 5px
  &__badge
    border-left: 3px solid var(--color-background-regular)
    border-right: 3px solid var(--color-background-regular)
    background-color: var(--color-gray-medium-light)
    border-radius: 5px
    color: var(--font-color-primary)
  &__remove-maneuver-text
    color: var(--color-table-body)
  &__remove-maneuver-icon
    cursor: pointer
    color: var(--color-table-body)
</style>
